import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import SubHero from '../other/SubHero';

const PrivacyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0rem 2rem;
`;

const Section = styled.section<{ isVisible: boolean }>`
  margin-bottom: 3rem;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s ease, transform 0.8s ease;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) => (props.isVisible ? 'translateY(0)' : 'translateY(20px)')};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  font-family: 'Garamond', serif;
  color: #13182a;
  position: relative;
  display: inline-block;
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -2px;
    left: 0;
    background-color: #13182a;
    transition: width 0.3s ease;
    height: 1.5px;
  }
  &:hover:after {
    width: 100%;
  }
`;

const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: #333;
  font-family: 'Garamond', sans-serif;
`;

const Separator = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(to right, transparent, #13182a, transparent);
  margin: 2rem 0;
`;

const PrivacyPage: React.FC = () => {
  // Load Inter font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <>
    <Helmet>
        <title>Heaviside | Privacy Policy</title>
        <meta name="description" content="Privacy Policy of Heaviside" />
        <meta name="author" content="Heaviside Team" />
        <meta property="og:title" content="Heaviside | Privacy Policy of Heaviside" />
        <meta property="og:description" content="Explore the privacy policy of Heaviside, the next-gen quant hedge fund challenging legacy institutions." />
        <meta property="og:url" content="https://hvside.com" />
        <meta property="og:image" content="https://hvside.com/logo_text_svg.svg" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Heaviside",
            "url": "https://hvside.com/privacy",
            "logo": "https://hvside.com/logo_text_svg.svg",
            "description": "This page explains the privacy rule of Heaviside website, a quantitative hedge fund.",
          }
          `}
          </script>
      </Helmet>
    <PrivacyContainer>
      <SubHero title="Privacy" />

      <Helmet>
        <title>Heaviside | Privacy Policy</title>
        <meta name="description" content="Privacy Policy of Heaviside" />
      </Helmet>

      <SectionWithVisibility>
        <SectionTitle>Information We Collect</SectionTitle>
        <Paragraph>
          At Heaviside, we collect information you provide directly to us, such as your name, email address, and any other details you submit through our contact forms. We may also collect data automatically through cookies and similar technologies.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>How We Use Your Information</SectionTitle>
        <Paragraph>
          We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to ensure the security of our platform. We do not share your personal information with third parties except as necessary to provide our services or as required by law.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>Data Security</SectionTitle>
        <Paragraph>
          We implement reasonable measures to protect your personal information from unauthorized access or disclosure. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>Your Rights</SectionTitle>
        <Paragraph>
          You have the right to access, update, or delete your personal information. If you have any questions about your data or wish to exercise your rights, please contact us at <a href="mailto:info@hvside.com"><strong><u>info@hvside.com</u></strong></a>
        </Paragraph>
      </SectionWithVisibility>
    </PrivacyContainer>
    </>
  );
};

// Wrapper component to handle visibility for fade-in animation
const SectionWithVisibility: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Section ref={ref} isVisible={inView}>
      {children}
    </Section>
  );
};



export default PrivacyPage;